<template>
  <div>
    <div class="overflow-x-auto">
      <!-- min-width, not width: the column widths are a floor, so a wide
           viewport still fills instead of leaving dead space to the right, and
           a narrow one scrolls rather than crushing every column. -->
      <table class="text-sm table-fixed w-full" :style="{ minWidth: tableWidth + 'px' }">
        <colgroup>
          <col v-for="col in columns" :key="col.key" :style="{ width: widthFor(col.key) + 'px' }" />
        </colgroup>
        <thead>
          <tr class="border-b border-gray-200 bg-gray-50">
            <th
              v-for="(col, ci) in columns"
              :key="col.key"
              class="relative px-4 py-2.5 text-left text-xs font-medium text-gray-500 whitespace-nowrap select-none group/th"
              :class="[
                col.sortable === false ? '' : 'cursor-pointer hover:text-gray-700',
                ci < columns.length - 1 ? 'border-r border-gray-200' : '',
              ]"
              @click="col.sortable === false ? null : $emit('sort', col.key)"
            >
              <span class="truncate">{{ col.label }}</span>
              <span v-if="sortField === col.key" class="ml-1">
                {{ sortDir === 'asc' ? '↑' : '↓' }}
              </span>
              <!-- Resize grip. Sits on the separator, widens on hover so it is
                   findable, and swallows the click so dragging never sorts. -->
              <span
                v-if="ci < columns.length - 1"
                class="absolute top-0 right-0 h-full w-2 translate-x-1/2 cursor-col-resize z-10 flex justify-center"
                :title="`Drag to resize — double-click to reset ${col.label || 'column'}`"
                @click.stop
                @mousedown.stop.prevent="startResize($event, col.key)"
                @dblclick.stop.prevent="resetWidth(col.key)"
              >
                <span
                  class="w-px h-full bg-transparent group-hover/th:bg-gray-300 transition-colors"
                  :class="resizingKey === col.key ? '!bg-primary-500' : ''"
                />
              </span>
            </th>
          </tr>
        </thead>
        <tbody>
          <!-- Ancestor-only rows (pulled in for context because a descendant
               matched, not because they matched) render muted: title in the
               same gray-400/chevron treatment Support_Row uses for its
               breadcrumb, and the result columns blank, so a context row is
               never mistaken for a hit. -->
          <tr
            v-for="item in pagedItems"
            :key="item.id"
            class="border-b border-gray-100 cursor-pointer transition-colors"
            :class="item.id === selectedId ? 'bg-primary-50' : 'hover:bg-gray-50'"
            @click="$emit('select', item)"
          >
            <!-- ID -->
            <td class="px-4 py-2.5 text-xs text-gray-400 whitespace-nowrap truncate">
              #{{ item.id }}
            </td>
            <!-- Title + tags -->
            <td class="px-4 py-2.5 overflow-hidden">
              <div class="flex items-center gap-1.5 flex-wrap">
                <span v-if="parentName(item.parent)" class="text-[10px] text-gray-400 truncate max-w-[120px]">
                  {{ parentName(item.parent) }}
                  <FontAwesomeIcon :icon="['fas', 'chevron-right']" class="text-[7px] mx-0.5" />
                </span>
                <span :class="isAncestorOnly(item) ? 'text-xs text-gray-400' : 'text-sm text-gray-900'">{{ item.title }}</span>
                <FontAwesomeIcon
                  v-if="isAncestorOnly(item)"
                  :icon="['fas', 'chevron-right']"
                  class="text-[8px] text-gray-300 shrink-0"
                />
                <span
                  v-for="tag in item.tags || []"
                  :key="tag"
                  class="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 shrink-0"
                >
                  {{ tag }}
                </span>
              </div>
            </td>
            <!-- Type -->
            <td class="px-4 py-2.5 whitespace-nowrap">
              <span
                class="text-[11px] font-medium px-1.5 py-0.5 rounded"
                :class="typeClass(item.requirement_type_name)"
              >
                {{ item.requirement_type_name }}
              </span>
            </td>
            <!-- Tenant -->
            <td class="px-4 py-2.5 whitespace-nowrap overflow-hidden">
              <span
                v-if="item.tenant_name"
                class="text-[11px] font-medium px-1.5 py-0.5 rounded truncate inline-block max-w-full align-middle"
                :style="tenantStyle(item.tenant_name)"
              >
                {{ item.tenant_name }}
              </span>
              <span v-else class="text-[11px] text-gray-300">—</span>
            </td>
            <!-- Status -->
            <td class="px-4 py-2.5 whitespace-nowrap">
              <span
                v-if="!isAncestorOnly(item)"
                class="text-xs px-2 py-0.5 rounded"
                :class="statusClass(item.status)"
              >
                {{ statusLabel(item.status) }}
              </span>
            </td>
            <!-- Priority -->
            <td class="px-4 py-2.5 whitespace-nowrap">
              <span
                v-if="!isAncestorOnly(item) && hasPriority(item.priority)"
                class="text-xs px-2 py-0.5 rounded"
                :class="priorityClass(item.priority)"
              >
                {{ priorityLabel(item.priority) }}
              </span>
            </td>
            <!-- Due Date -->
            <td class="px-4 py-2.5 whitespace-nowrap text-xs" :class="isAncestorOnly(item) ? '' : dueDateClass(item.due_date)">
              {{ isAncestorOnly(item) ? '' : formatDueDate(item.due_date) }}
            </td>
            <!-- Parent -->
            <td class="px-4 py-2.5 text-xs truncate" :class="isAncestorOnly(item) ? 'text-gray-400' : 'text-gray-500'">
              {{ parentName(item.parent) }}
            </td>
            <!-- Owner -->
            <td class="px-4 py-2.5 text-xs text-gray-500 whitespace-nowrap truncate">
              {{ isAncestorOnly(item) ? '' : item.owner_name || '' }}
            </td>
            <!-- Requester -->
            <td class="px-4 py-2.5 text-xs text-gray-500 whitespace-nowrap truncate">
              {{ isAncestorOnly(item) ? '' : item.requester_name || '' }}
            </td>
            <!-- Comments -->
            <td class="px-4 py-2.5 whitespace-nowrap">
              <span
                v-if="!isAncestorOnly(item) && commentCounts[item.id]"
                class="inline-flex items-center gap-1 text-xs text-gray-400"
              >
                <FontAwesomeIcon :icon="['far', 'comment']" class="text-[11px]" />
                {{ commentCounts[item.id] }}
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Pagination -->
    <div v-if="totalPages > 1" class="flex items-center justify-between px-4 py-3 border-t border-gray-200 text-xs text-gray-500">
      <span>{{ (currentPage - 1) * pageSize + 1 }}–{{ Math.min(currentPage * pageSize, items.length) }} of {{ items.length }}</span>
      <div class="flex gap-1">
        <button
          v-for="page in pageRange"
          :key="page"
          class="px-2 py-1 rounded"
          :class="page === currentPage ? 'bg-primary-500 text-white' : 'hover:bg-gray-100'"
          @click="currentPage = page"
        >
          {{ page }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { z } from 'zod';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
  daysUntil,
  isDueThisBusinessWeek,
  formatDateValue,
  priorityClass as priorityClassFor,
  priorityShortLabel,
  priorityToCode,
  tenantColorFor,
} from './support_constants';
import { BrowserStore } from '@/utils/BrowserStore';

// Column widths survive a reload but are not part of the filter state -- they
// describe the viewport, not the query.
const MIN_COLUMN_WIDTH = 56;

const COLUMNS = [
  { key: 'id', label: 'ID', width: 80 },
  { key: 'title', label: 'Title', width: 380 },
  { key: 'requirement_type_name', label: 'Type', width: 120 },
  { key: 'tenant_name', label: 'Tenant', width: 130 },
  { key: 'status', label: 'Status', width: 110 },
  { key: 'priority', label: 'Priority', width: 90 },
  { key: 'due_date', label: 'Due Date', width: 120 },
  { key: 'parent', label: 'Parent', width: 180 },
  { key: 'owner_name', label: 'Owner', width: 130 },
  { key: 'requester_name', label: 'Requester', width: 130 },
  { key: 'comments', label: '', width: 70, sortable: false },
];

// Viewport state, deliberately not part of the filter state -- see
// CareManagement_PatientTable's cm_patient_table_col_widths for the same shape.
// The schema is derived from COLUMNS so the per-column default lives in exactly
// one place: a stored payload missing a key (a column added later) resolves to
// that column's default, and an unknown key (a column removed) is stripped.
const colWidthsStore = new BrowserStore(
  'support_list_column_widths',
  z.object(
    Object.fromEntries(
      COLUMNS.map((c) => [
        c.key,
        // .catch before .default so ONE bad stored value degrades to that
        // column's width instead of discarding every column's. .min keeps a
        // hand-edited 5px column from rendering unusable.
        z.number().min(MIN_COLUMN_WIDTH).catch(c.width).default(c.width),
      ]),
    ),
  ),
);

const STATUS_CLASSES = {
  draft: 'bg-gray-100 text-gray-600',
  approved: 'bg-blue-50 text-blue-700',
  in_progress: 'bg-amber-50 text-amber-700',
  done: 'bg-emerald-50 text-emerald-700',
  open: 'bg-red-50 text-red-700',
  waiting: 'bg-orange-50 text-orange-700',
  resolved: 'bg-emerald-50 text-emerald-700',
  closed: 'bg-gray-100 text-gray-500',
};

const STATUS_LABELS = {
  draft: 'Draft',
  approved: 'Approved',
  in_progress: 'In Progress',
  done: 'Done',
  open: 'Open',
  waiting: 'Waiting',
  resolved: 'Resolved',
  closed: 'Closed',
};

const TYPE_CLASSES = {
  'Epic': 'bg-indigo-50 text-indigo-700',
  'Feature': 'bg-blue-50 text-blue-700',
  'Requirement': 'bg-emerald-50 text-emerald-700',
  'Bug': 'bg-red-50 text-red-700',
  'Support Ticket': 'bg-amber-50 text-amber-700',
  'Fast-Follow': 'bg-violet-50 text-violet-700',
};

export default {
  name: 'Support_Table',
  components: { FontAwesomeIcon },
  props: {
    items: { type: Array, required: true },
    allItems: { type: Array, required: true },
    selectedId: { type: Number, default: null },
    commentCounts: { type: Object, default: () => ({}) },
    filterMatchIds: { type: Set, default: () => new Set() },
    hasActiveFilters: { type: Boolean, default: false },
    // Sort is owned by Support_List so the tree and the table can't disagree.
    // This component renders `items` in the order it is handed them.
    sortField: { type: String, default: null },
    sortDir: { type: String, default: 'asc' },
  },
  emits: ['select', 'sort'],
  data() {
    return {
      currentPage: 1,
      pageSize: 50,
      resizingKey: null,
      // Dense: every column always has a width, defaulted by the schema.
      columnWidths: colWidthsStore.load(),
      columns: COLUMNS,
    };
  },
  computed: {
    parentMap() {
      const map = {};
      for (const item of this.allItems) {
        map[item.id] = item.title;
      }
      return map;
    },
    tableWidth() {
      return this.columns.reduce((sum, col) => sum + this.widthFor(col.key), 0);
    },
    totalPages() {
      return Math.ceil(this.items.length / this.pageSize);
    },
    pagedItems() {
      const start = (this.currentPage - 1) * this.pageSize;
      return this.items.slice(start, start + this.pageSize);
    },
    pageRange() {
      const range = [];
      const delta = 2;
      const left = Math.max(1, this.currentPage - delta);
      const right = Math.min(this.totalPages, this.currentPage + delta);
      for (let i = left; i <= right; i++) range.push(i);
      return range;
    },
  },
  watch: {
    items() {
      this.currentPage = 1;
    },
  },
  beforeUnmount() {
    this.stopResize();
  },
  methods: {
    widthFor(key) {
      return this.columnWidths[key];
    },
    startResize(event, key) {
      this.resizingKey = key;
      this._resizeStartX = event.clientX;
      this._resizeStartWidth = this.widthFor(key);
      window.addEventListener('mousemove', this.onResizeMove);
      window.addEventListener('mouseup', this.stopResize);
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'col-resize';
    },
    onResizeMove(event) {
      if (!this.resizingKey) return;
      const next = Math.max(
        MIN_COLUMN_WIDTH,
        Math.round(this._resizeStartWidth + (event.clientX - this._resizeStartX)),
      );
      this.columnWidths = { ...this.columnWidths, [this.resizingKey]: next };
    },
    stopResize() {
      window.removeEventListener('mousemove', this.onResizeMove);
      window.removeEventListener('mouseup', this.stopResize);
      if (!this.resizingKey) return;
      this.resizingKey = null;
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
      colWidthsStore.save(this.columnWidths);
    },
    resetWidth(key) {
      const fallback = COLUMNS.find((c) => c.key === key)?.width;
      if (fallback === undefined) return;
      this.columnWidths = { ...this.columnWidths, [key]: fallback };
      colWidthsStore.save(this.columnWidths);
    },
    tenantStyle(name) {
      return tenantColorFor(name);
    },
    parentName(parentId) {
      if (!parentId) return '';
      return this.parentMap[parentId] || '';
    },
    // A row that only survived the filters because a descendant matched --
    // same predicate Support_Row.isAncestorOnly uses, so tree and table view
    // agree on which rows are context rather than results.
    isAncestorOnly(item) {
      return this.hasActiveFilters && !this.filterMatchIds.has(item.id);
    },
    statusClass(status) {
      return STATUS_CLASSES[status] || STATUS_CLASSES.draft;
    },
    statusLabel(status) {
      return STATUS_LABELS[status] || status;
    },
    priorityClass(priority) {
      return priorityClassFor(priority);
    },
    priorityLabel(priority) {
      return priorityShortLabel(priority);
    },
    hasPriority(priority) {
      return priorityToCode(priority) !== 'none';
    },
    typeClass(type) {
      return TYPE_CLASSES[type] || 'bg-gray-100 text-gray-600';
    },
    dueDateClass(date) {
      if (!date) return 'text-gray-500';
      const diff = daysUntil(date);
      if (diff == null) return 'text-gray-500';
      if (diff < 0) return 'text-red-500 font-medium';
      if (diff <= 3) return 'text-amber-500 font-medium';
      if (isDueThisBusinessWeek(date)) return 'text-yellow-500 font-medium';
      return 'text-gray-500';
    },
    formatDueDate(date) {
      return date ? formatDateValue(date) : '';
    },
  },
};
</script>
